import React from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { css } from '@emotion/core';
import TextField from '@material-ui/core/TextField';
import withSettings from 'editor/components/FormContent/Field/withSettings';
import Dropdown from 'client/components/FormContent/Field/DropdownSelect/Dropdown';
import useFocus from 'client/components/FormContent/Field/fieldState';
import {
    fieldWrapperStyle,
    generateMuiSelectCustomColorStyle,
    generateMuiTextFieldCustomColorStyle
} from 'common/style/fields.style';
import { generateSectionStyle } from 'common/style/cssStyleType';
import { error } from 'common/components/Mui/styles/color';
import NameDescription from 'common/components/NameDescription';
import { below, above } from 'common/utils/breakpoint';
import { OPTIONS_TYPE } from 'common/utils/config';

function FieldSingleDropOption({
    fieldId,
    displayName,
    description,
    placeholder,
    isRequired,
    enableOptionsLimit,
    showRest,
    settingStyle,
    value,
    updateFormValue,
    touched,
    setFieldTouched,
    error,
    handleBlur
}) {
    const [focus, toggleError] = useFocus(false);
    const { displayNameStyle, descStyle, answerStyle } = generateSectionStyle(settingStyle);
    const options = useSelector(state => {
        return state.options.reduce((arr, option) => {
            const cloneOption = { ...option };
            const optionSettings = state.settings.options.find(item => item.id === option.id);
            if (optionSettings) cloneOption.setting = { ...optionSettings };
            if ((error?.optionId ?? []).find(id => id === option.id)) {
                cloneOption.error = error.message;
            }

            if (option.fieldId === fieldId && !option.deleted) {
                arr.push(cloneOption);
            }
            return arr;
        }, []);
    });

    const selectedOption = value[fieldId] || { optionId: '', label: '' };
    const selectedOptionData = options.find(option => option.id === selectedOption.optionId);
    const isOtherOption = selectedOptionData?.type === OPTIONS_TYPE.NORMAL_OTHER;

    function handleSelect(optionId, label) {
        const selectedOptionData = options.find(option => option.id === optionId);
        const isOtherOption = selectedOptionData?.type === OPTIONS_TYPE.NORMAL_OTHER;
        if (isOtherOption) {
            updateFormValue({ [fieldId]: { optionId, label: '' } });
        } else {
            updateFormValue({ [fieldId]: { optionId, label } });
        }
        setFieldTouched(fieldId);
    }

    const handleOtherInputChange = (e, optionId) => {
        const payload = {
            label: e.target.value,
            optionId: optionId
        };
        updateFormValue({ [fieldId]: payload });
    };

    const showFieldError = !focus && touched && error && typeof error === 'string';
    const showOptionError = !focus && touched && error && typeof error === 'object';

    return (
        <div css={css(fieldWrapperStyle)} id={`section_${fieldId}`}>
            <NameDescription
                displayName={displayName}
                description={description}
                displayNameStyle={displayNameStyle}
                descStyle={descStyle}
                isRequired={isRequired}
            />
            <div css={dropdownWrapperStyle}>
                <Dropdown
                    value={selectedOption?.optionId}
                    placeholder={placeholder}
                    style={generateMuiSelectCustomColorStyle(answerStyle)}
                    optionStyle={optionStyle}
                    onSelect={handleSelect}
                    items={options}
                    error={showOptionError ? error.message : false}
                    enableOptionsLimit={enableOptionsLimit}
                    showRest={showRest}
                    answerStyle={answerStyle}
                />
                {isOtherOption && (
                    <TextField
                        css={css([generateMuiTextFieldCustomColorStyle(answerStyle)])}
                        placeholder={i18next.t('section.answer.short.placeholder')}
                        onFocus={() => toggleError(true)}
                        onBlur={e => toggleError(false, handleBlur, e)}
                        onChange={e => handleOtherInputChange(e, selectedOption.optionId)}
                        value={value[fieldId]?.label || ''}
                        error={showFieldError}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                    />
                )}
            </div>
            {showFieldError && (
                <span css={css(errorTextStyle)} id="feedback">
                    {error}
                </span>
            )}
        </div>
    );
}

FieldSingleDropOption.propTypes = {
    fieldId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    displayName: PropTypes.string,
    description: PropTypes.string,
    placeholder: PropTypes.string,
    error: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            message: PropTypes.string,
            optionId: PropTypes.arrayOf(PropTypes.number)
        })
    ]),
    touched: PropTypes.bool,
    updateFormValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    settingStyle: PropTypes.object,
    isRequired: PropTypes.bool,
    value: PropTypes.object
};

export default withSettings(FieldSingleDropOption);

const errorTextStyle = `
    text-align: right;
    font-size: 0.8125rem;
    line-height: 1.1875rem;
    color: ${error.color};
    float: right;
`;

const dropdownWrapperStyle = css`
    display: flex;
    gap: 16px;
    align-items: flex-start;

    ${below('sm')} {
        flex-direction: column;
    }

    & > * {
        width: 100%;
        flex: 1;

        ${above('sm')} {
            width: 50%;
            flex: 0.5;
        }
    }
`;

const optionStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    whiteSpace: 'pre-wrap',
    padding: '0.5rem 1rem',
    maxWidth: '400px'
};
